import {
  BookingPackageFlight,
  BookingPackageFlightMetaDataLine,
} from "@qite/tide-client/build/types";
import { BookingPackageOption } from "@qite/tide-client/build/types/offer/booking-v2/shared/booking-package-option";
import { differenceInHours, format, isSameDay, parse } from "date-fns";
import { graphql, useStaticQuery } from "gatsby";
import { cloneDeep, first, last, uniq, uniqBy } from "lodash";
import React, { useEffect, useState } from "react";
import { useProductStore } from "../../app/search-results/product-store/context";
import { setFlightsFlyInIsOpen } from "../../app/search-results/product-store/reducer";
import { useQSMStore } from "../../app/search-results/qsm-store/context";
import { setBookingPackage, setFlight, setRooms } from "../../app/search-results/qsm-store/reducer";
import { TideItemForAirline } from "../../types";
import { formatter } from "../../utils";
import Icon from "../icon";
import FlyInWarnings from "./fly-in-warnings";
import { useI18next } from "gatsby-plugin-react-i18next";

interface FlyInFlightProps {
  destination: string;
}

interface Airport {
  code: string;
  name?: string;
}

export interface flightPair {
  outward: BookingPackageFlight;
  return: BookingPackageFlight;
}

const FlightsFlyIn: React.FC<FlyInFlightProps> = ({ destination }) => {
  const { t } = useI18next();
  const [productState, productDispatch] = useProductStore();
  const [qsmState, qsmDispatch] = useQSMStore();

  const [travelClasses, setTravelClasses] = useState<string[]>([]);
  const [selectedTravelClass, setSelectedTravelClass] = useState<string>();

  const [departureAirports, setDepartureAirports] = useState<Airport[]>([]);
  const [selectedDepartureAirport, setSelectedDepartureAirport] = useState<Airport>();

  const [flightPairs, setFlightPairs] = useState<flightPair[]>([]);
  const [filteredFlightPairs, setFilteredFlightPairs] = useState<flightPair[]>([]);
  const [selectedFlightPair, setSelectedFlightPair] = useState<flightPair>();
  const [currentOption, setCurrentOption] = useState<string>();

  const queryResult = useStaticQuery(graphql`
    query airlineDataQuery {
      allTideItemForAirline {
        nodes {
          content {
            general {
              name
              icon {
                url
                title
                altText
              }
            }
            descriptive {
              data {
                iata
              }
            }
          }
        }
      }
    }
  `);

  const handleClose = () => {
    if (productDispatch) productDispatch(setFlightsFlyInIsOpen(false));
  };

  const confirmOptions = () => {
    if (qsmDispatch && selectedFlightPair) {
      qsmDispatch(setFlight(selectedFlightPair));

      const bookingPackage = cloneDeep(qsmState?.bookingPackage);

      if (bookingPackage) {
        const selectedOption = bookingPackage.options.find((x) => x.isSelected);
        const optionShouldBeChanged = !selectedFlightPair.outward.validOptions.some(
          (x) => x === selectedOption?.id
        );

        if (selectedOption && optionShouldBeChanged) {
          const newOption = bookingPackage.options.find((x) =>
            selectedFlightPair.outward.validOptions.some((y) => y === x.id)
          );
          if (newOption && newOption.id !== selectedOption.id) {
            selectedOption.isSelected = false;
            newOption.isSelected = true;

            setCurrentOption(newOption.id);

            var rooms = cloneDeep(qsmState?.rooms);
            if (rooms && rooms.length) {
              rooms.forEach((r, i) => {
                if (r.accommodationCode) {
                  const room = newOption.rooms[i];

                  const oldRoomOption = room.options.find((x) => x.isSelected);
                  if (oldRoomOption) {
                    const newRoomOption = room.options.find(
                      (x) =>
                        x.accommodationCode === r.accommodationCode && x.regimeCode == r.regimeCode
                    );

                    if (!newRoomOption) {
                      r.accommodationCode = oldRoomOption.accommodationCode;
                      r.regimeCode = oldRoomOption.regimeCode;
                    } else if (oldRoomOption.entryLineGuid !== newRoomOption.entryLineGuid) {
                      oldRoomOption.isSelected = false;
                      newRoomOption.isSelected = true;
                    }
                  }
                }
              });

              qsmDispatch(setRooms(rooms));
            }
          }
        }

        const oldOutwardFlight = bookingPackage.outwardFlights.find((x) => x.isSelected);
        const newOutwardFlight = bookingPackage.outwardFlights.find(
          (x) => x.entryLineGuid === selectedFlightPair.outward.entryLineGuid
        );

        if (
          oldOutwardFlight !== undefined &&
          newOutwardFlight !== undefined &&
          oldOutwardFlight?.entryLineGuid !== newOutwardFlight?.entryLineGuid
        ) {
          oldOutwardFlight.isSelected = false;
          newOutwardFlight.isSelected = true;
        }

        const oldReturnFlight = bookingPackage.returnFlights.find((x) => x.isSelected);
        const newReturnFlight = bookingPackage.returnFlights.find(
          (x) => x.entryLineGuid === selectedFlightPair.return.entryLineGuid
        );

        if (
          oldReturnFlight !== undefined &&
          newReturnFlight !== undefined &&
          oldReturnFlight?.entryLineGuid !== newReturnFlight?.entryLineGuid
        ) {
          oldReturnFlight.isSelected = false;
          newReturnFlight.isSelected = true;
        }
        qsmDispatch(setBookingPackage(bookingPackage));
      }
    }

    handleClose();
  };

  const getTotalPriceDiff = () => {
    const originalOutwardFlight = qsmState?.bookingPackage?.outwardFlights.find(
      (x) => x.isSelected
    );
    const originalReturnFlight = qsmState?.bookingPackage?.returnFlights.find((x) => x.isSelected);
    const selectedOutwardFlight = selectedFlightPair?.outward;
    const selectedReturnFlight = selectedFlightPair?.return;
    const flightPriceDiff =
      (selectedOutwardFlight?.price ?? 0) -
      (originalOutwardFlight?.price ?? 0) +
      (selectedReturnFlight?.price ?? 0) -
      (originalReturnFlight?.price ?? 0);

    const originalOption = qsmState?.bookingPackage?.options.find((x) => x.isSelected);
    const selectedOption = qsmState?.bookingPackage?.options.find((x) =>
      selectedOutwardFlight?.validOptions.includes(x.id)
    );
    const originalOptionPrice = originalOption ? calcHotelPrice(originalOption) : 0;
    const selectedOptionPrice = selectedOption ? calcHotelPrice(selectedOption) : 0;
    const optionPriceDiff = selectedOptionPrice - originalOptionPrice;

    return flightPriceDiff + optionPriceDiff;
  };

  const getPriceDifference = (airportCode?: string, flight?: flightPair) => {
    if (airportCode) {
      const selectedFlight =
        selectedFlightPair?.outward ??
        qsmState?.bookingPackage?.outwardFlights.find((x) => x.isSelected);
      const returnFlight =
        selectedFlightPair?.return ??
        qsmState?.bookingPackage?.returnFlights.find((x) => x.isSelected);

      let originalPrice = 0;
      if (selectedFlight && returnFlight) {
        originalPrice = selectedFlight.price + returnFlight.price;
      }

      let cheapestOutwardFlight = first(
        qsmState?.bookingPackage?.outwardFlights
          .filter(
            (x) =>
              x.flightMetaData.flightLines[0]?.departureAirport == airportCode &&
              x.flightMetaData.flightLines[0]?.travelClass == selectedTravelClass
          )
          .sort((a, b) => a.price - b.price)
      );

      let matchingReturnFlight = cheapestOutwardFlight?.externalGuid
        ? qsmState?.bookingPackage?.returnFlights.find(
            (x) => x.externalGuid == cheapestOutwardFlight?.externalGuid
          )
        : selectedTravelClass
        ? qsmState?.bookingPackage?.returnFlights.find(
            (x) =>
              first(x.flightMetaData.flightLines)?.departureAirport ==
                last(cheapestOutwardFlight?.flightMetaData.flightLines)?.arrivalAirport &&
              first(x.flightMetaData.flightLines)?.travelClass == selectedTravelClass &&
              last(x.flightMetaData.flightLines)?.arrivalAirport ==
                first(cheapestOutwardFlight?.flightMetaData.flightLines)?.departureAirport
          )
        : qsmState?.bookingPackage?.returnFlights.find(
            (x) =>
              first(x.flightMetaData.flightLines)?.departureAirport ==
                last(cheapestOutwardFlight?.flightMetaData.flightLines)?.arrivalAirport &&
              last(x.flightMetaData.flightLines)?.arrivalAirport ==
                first(cheapestOutwardFlight?.flightMetaData.flightLines)?.departureAirport
          );

      const selectedHotel = qsmState?.bookingPackage?.options.find((x) =>
        selectedFlight?.validOptions.includes(x.id)
      );
      const matchingHotel = qsmState?.bookingPackage?.options.find((x) =>
        cheapestOutwardFlight?.validOptions.includes(x.id)
      );
      const selectedHotelPrice = selectedHotel ? calcHotelPrice(selectedHotel) : 0;
      const matchingHotelPrice = matchingHotel ? calcHotelPrice(matchingHotel) : 0;
      const hotelPriceDifference =
        selectedFlight == cheapestOutwardFlight ? 0 : matchingHotelPrice - selectedHotelPrice;

      if (flight) {
        cheapestOutwardFlight = flight.outward;
        matchingReturnFlight = flight.return;
      }

      let cheapestPriceForAirport = 0;
      if (cheapestOutwardFlight && matchingReturnFlight) {
        cheapestPriceForAirport = cheapestOutwardFlight.price + matchingReturnFlight.price;
      }

      return cheapestPriceForAirport - originalPrice + hotelPriceDifference;
    }

    return 0;
  };

  const selectDepartureAirport = (airportCode: string, travelClass?: string) => {
    setSelectedDepartureAirport(departureAirports.find((x) => x.code == airportCode));
    setSelectedTravelClass(travelClass);

    if (qsmState?.bookingPackage) {
      let desiredFlight = qsmState?.bookingPackage.outwardFlights.find(
        (x) => first(x.flightMetaData.flightLines)?.departureAirport == airportCode
      );
      if (travelClass) {
        desiredFlight = qsmState?.bookingPackage.outwardFlights.find(
          (x) =>
            first(x.flightMetaData.flightLines)?.departureAirport == airportCode &&
            first(x.flightMetaData.flightLines)?.travelClass == travelClass
        );
      }
      if (desiredFlight) {
        let matchingReturnFlight = desiredFlight?.externalGuid
          ? qsmState?.bookingPackage?.returnFlights.find(
              (x) => x.externalGuid == desiredFlight?.externalGuid
            )
          : travelClass
          ? qsmState?.bookingPackage?.returnFlights.find(
              (x) =>
                first(x.flightMetaData.flightLines)?.departureAirport ==
                  last(desiredFlight?.flightMetaData.flightLines)?.arrivalAirport &&
                first(x.flightMetaData.flightLines)?.travelClass == travelClass &&
                last(x.flightMetaData.flightLines)?.arrivalAirport ==
                  first(desiredFlight?.flightMetaData.flightLines)?.departureAirport &&
                desiredFlight?.validOptions.sort().join(",") === x.validOptions.sort().join(",")
            )
          : qsmState?.bookingPackage?.returnFlights.find(
              (x) =>
                first(x.flightMetaData.flightLines)?.departureAirport ==
                  last(desiredFlight?.flightMetaData.flightLines)?.arrivalAirport &&
                last(x.flightMetaData.flightLines)?.arrivalAirport ==
                  first(desiredFlight?.flightMetaData.flightLines)?.departureAirport &&
                desiredFlight?.validOptions.sort().join(",") === x.validOptions.sort().join(",")
            );

        if (matchingReturnFlight) {
          setSelectedFlightPair({
            outward: desiredFlight,
            return: matchingReturnFlight,
          });
        }
      }
    }
  };

  const calcHotelPrice = (bookingPackageOption: BookingPackageOption) => {
    const selectedOption = qsmState?.bookingPackage?.options.find((x) => x.isSelected);
    return bookingPackageOption?.rooms
      ?.map(
        (room, index) =>
          room.options.find(
            (option) =>
              option.accommodationCode ==
                selectedOption?.rooms[index].options.find((x) => x.isSelected)?.accommodationCode &&
              option.regimeCode ==
                selectedOption?.rooms[index].options.find((x) => x.isSelected)?.regimeCode
          )?.price ?? 0
      )
      .reduce((a, b) => a + b);
  };

  const selectFlight = (flight: flightPair) => {
    setSelectedFlightPair(flightPairs.find((x) => x == flight));
  };

  const getFlightPriceDifference = () => {
    if (qsmState?.bookingPackage) {
      let originalFlightsPrice = 0;
      let desiredFlightsPrice = 0;

      originalFlightsPrice +=
        qsmState?.bookingPackage.outwardFlights.find((x) => x.isSelected)?.price ?? 0;
      originalFlightsPrice +=
        qsmState?.bookingPackage.returnFlights.find((x) => x.isSelected)?.price ?? 0;

      desiredFlightsPrice += selectedFlightPair?.outward.price ?? 0;
      desiredFlightsPrice += selectedFlightPair?.return.price ?? 0;

      if (desiredFlightsPrice && originalFlightsPrice) {
        return desiredFlightsPrice - originalFlightsPrice;
      }
    }
    return 0;
  };

  const getRoomPriceDifference = () => {
    const activeOption = qsmState?.bookingPackage?.options.find((x) => x.isSelected);
    if (activeOption?.rooms && qsmState?.rooms) {
      let originalOptionsPrice = 0;
      let desiredOptionsPrice = 0;
      for (let index = 0; index < qsmState?.rooms.length; index++) {
        const room = activeOption.rooms[index];

        const originalOption = room?.options?.find((x) => x.isSelected);
        originalOptionsPrice += originalOption?.price ?? 0;

        const desiredOption = room?.options?.find(
          (x) =>
            x.accommodationCode == qsmState?.rooms[index].accommodationCode &&
            x.regimeCode == qsmState?.rooms[index].regimeCode
        );
        desiredOptionsPrice += desiredOption ? desiredOption.price : originalOption?.price ?? 0;
      }
      if (desiredOptionsPrice && originalOptionsPrice) {
        return desiredOptionsPrice - originalOptionsPrice;
      }
    }
    return 0;
  };

  const getTotalPriceDifference = () => {
    const flightPriceDifference = getFlightPriceDifference();
    const roomPriceDifference = getRoomPriceDifference();

    return flightPriceDifference + roomPriceDifference;
  };

  const getAirlineIcon = (flight: BookingPackageFlight) => {
    let longestFlightLine = first(flight.flightMetaData.flightLines);
    let longestFlightLinehours = 0;

    for (const line of flight.flightMetaData.flightLines) {
      let startDate = new Date(line.departureDate);
      const departureTimes = line.departureTime.split(":");
      startDate.setHours(Number(first(departureTimes)));
      startDate.setMinutes(Number(last(departureTimes)));

      let endDate = new Date(line.arrivalDate);
      const arrivalTimes = line.arrivalTime.split(":");
      endDate.setHours(Number(first(arrivalTimes)));
      endDate.setMinutes(Number(last(arrivalTimes)));

      const difference = differenceInHours(endDate, startDate);
      if (difference > longestFlightLinehours) {
        longestFlightLinehours = difference;
        longestFlightLine = line;
      }
    }

    const iata = longestFlightLine?.operatingAirlineCode ?? longestFlightLine?.airlineCode;

    if (iata) {
      const airline = (queryResult.allTideItemForAirline.nodes as TideItemForAirline[]).find(
        (x) => x.content?.descriptive?.data?.iata == iata
      );
      const imageSrc = airline?.content?.general?.icon?.url;
      if (imageSrc) {
        return imageSrc;
      }
    }

    return undefined;
  };

  const getWarnings = (
    flight: flightPair,
    flightLine: BookingPackageFlightMetaDataLine,
    isOutward: boolean
  ) => {
    const warnings = [];
    const flightItem = isOutward ? flight.outward : flight.return;

    const isLastLine = flightLine == last(flightItem.flightMetaData.flightLines);
    const isLandingSameDay = isSameDay(
      new Date(first(flightItem.flightMetaData.flightLines)!.departureDate),
      new Date(last(flightItem.flightMetaData.flightLines)!.arrivalDate)
    );

    if (isLastLine && !isLandingSameDay) {
      warnings.push(t("IS_OVERNIGHT_FLIGHT"));
    }

    if (!flightItem.validOptions.some((x) => x == currentOption)) {
      warnings.push(t("FLIGHT_CHANGES_HOTEL_DATES"));
    }

    return warnings;
  };

  useEffect(() => {
    if (!qsmState?.bookingPackage) return;

    const CLASS_ORDER = ["Economy", "PremiumEconomy", "Business", "First"] as const;

    const classes = uniq(
      qsmState?.bookingPackage?.outwardFlights
        .map((x) => first(x.flightMetaData.flightLines)?.travelClass)
        .concat(
          qsmState?.bookingPackage?.returnFlights.map(
            (x) => first(x.flightMetaData.flightLines)?.travelClass
          )
        )
    ) as string[];

    // if all values are recognised, sort them; otherwise keep the original order
    const orderedClasses = classes.every((c) =>
      CLASS_ORDER.includes(c as typeof CLASS_ORDER[number])
    )
      ? [...classes].sort(
          (a, b) =>
            CLASS_ORDER.indexOf(a as typeof CLASS_ORDER[number]) -
            CLASS_ORDER.indexOf(b as typeof CLASS_ORDER[number])
        )
      : classes;
    setTravelClasses(orderedClasses);
    setSelectedTravelClass(first(orderedClasses));

    const airports = uniqBy(
      qsmState?.bookingPackage?.outwardFlights.map((x) => {
        return {
          code: x.flightMetaData.flightLines[0]?.departureAirport,
          name: x.flightMetaData.flightLines[0]?.departureAirportDescription,
        } as Airport;
      }),
      "code"
    );
    const selectedAirport = qsmState?.bookingPackage?.outwardFlights.find((x) => x.isSelected)
      ?.flightMetaData.flightLines[0]?.departureAirport;
    if (selectedAirport) {
      setSelectedDepartureAirport({
        code: selectedAirport,
        name: qsmState?.bookingPackage?.outwardFlights.find((x) => x.isSelected)?.flightMetaData
          .flightLines[0]?.departureAirportDescription,
      });
      let position = airports.find((x) => x.code == selectedAirport);
      if (position) airports.unshift(airports.splice(airports.indexOf(position), 1)[0]);
    }

    setDepartureAirports(airports);
    setSelectedDepartureAirport(first(airports));

    let flights = [] as flightPair[];
    if (qsmState?.bookingPackage?.outwardFlights) {
      for (const outwardFlight of qsmState.bookingPackage.outwardFlights) {
        let matchingReturnFlight = outwardFlight?.externalGuid
          ? qsmState?.bookingPackage?.returnFlights.find(
              (x) => x.externalGuid == outwardFlight?.externalGuid
            )
          : selectedTravelClass
          ? qsmState?.bookingPackage?.returnFlights.find(
              (x) =>
                first(x.flightMetaData.flightLines)?.departureAirport ==
                  last(outwardFlight?.flightMetaData.flightLines)?.arrivalAirport &&
                first(x.flightMetaData.flightLines)?.travelClass == selectedTravelClass &&
                last(x.flightMetaData.flightLines)?.arrivalAirport ==
                  first(outwardFlight?.flightMetaData.flightLines)?.departureAirport &&
                outwardFlight?.validOptions.sort().join(",") === x.validOptions.sort().join(",")
            )
          : qsmState?.bookingPackage?.returnFlights.find(
              (x) =>
                first(x.flightMetaData.flightLines)?.departureAirport ==
                  last(outwardFlight?.flightMetaData.flightLines)?.arrivalAirport &&
                last(x.flightMetaData.flightLines)?.arrivalAirport ==
                  first(outwardFlight?.flightMetaData.flightLines)?.departureAirport &&
                outwardFlight?.validOptions.sort().join(",") === x.validOptions.sort().join(",")
            );
        if (matchingReturnFlight) {
          const flight = { outward: outwardFlight, return: matchingReturnFlight } as flightPair;
          flights.push(flight);

          if (flight.outward.isSelected) setSelectedFlightPair(flight);
        }
      }
    }

    setCurrentOption(qsmState.bookingPackage.options.find((x) => x.isSelected)?.id);
    setFlightPairs(flights);
  }, [qsmState?.bookingPackage]);

  useEffect(() => {
    let flights = [...flightPairs];
    flights = flights.filter(
      (x) =>
        first(x.outward.flightMetaData.flightLines)?.departureAirport ==
          selectedDepartureAirport?.code &&
        first(x.outward.flightMetaData.flightLines)!.travelClass == selectedTravelClass
    );
    setFilteredFlightPairs(flights);
  }, [selectedDepartureAirport, selectedTravelClass]);

  useEffect(() => {
    if (productState?.flightsFlyInIsOpen && qsmState?.flight) {
      const copy = { ...qsmState.flight } as flightPair;
      setSelectedFlightPair(copy);
    }
  }, [productState?.flightsFlyInIsOpen]);

  return (
    <div className={`fly-in ${productState?.flightsFlyInIsOpen ? "fly-in--active" : ""}`}>
      <div className="fly-in__backdrop" onClick={() => handleClose()}></div>
      <div className="fly-in__frame">
        <div className="fly-in__header">
          <div className="fly-in__close" onClick={() => handleClose()}>
            <Icon name="ui-close" />
          </div>
          <div className="fly-in__title">
            {t("CHOOSE_ANOTHER_FLIGHT_FOR_DESTINATION", { destination })}
          </div>
        </div>
        <div className="fly-in__body">
          <div className="fly-in__item">
            <div className="fly-in__item-title">{t("SHOW_FLIGHTS_IN_FLIGHTCLASS")}</div>
            <div className="fly-in__item-content">
              <div className="travel-class">
                {travelClasses &&
                  travelClasses.map((travelClass: string, index: number) => (
                    <div
                      key={index}
                      className={`travel-class__item ${
                        travelClass == selectedTravelClass ? "travel-class__item--active" : ""
                      }`}
                      onClick={() =>
                        selectDepartureAirport(selectedDepartureAirport?.code!, travelClass)
                      }
                    >
                      <a
                        href="#"
                        className="configure-rooms__check"
                        onClick={(e) => e.preventDefault()}
                      >
                        {travelClass == selectedTravelClass && (
                          <span className="configure-rooms__check--active">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="10"
                              height="10"
                              fill="currentColor"
                              className="bi bi-circle-fill"
                              viewBox="0 0 16 16"
                            >
                              <circle cx="8" cy="8" r="8" />
                            </svg>
                          </span>
                        )}
                      </a>
                      <div className="travel-class__airport">{travelClass}</div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
          <div className="fly-in__item">
            <div className="fly-in__item-title">{t("SHOW_FLIGHTS_FROM")}</div>
            <div className="fly-in__item-content">
              <div className="departure-airport">
                {departureAirports &&
                  departureAirports.map((airport, index) => (
                    <div
                      key={index}
                      className={`departure-airport__item ${
                        airport.code == selectedDepartureAirport?.code
                          ? "departure-airport__item--active"
                          : ""
                      }`}
                      onClick={() => selectDepartureAirport(airport.code, selectedTravelClass)}
                    >
                      <a
                        href="#"
                        className="configure-rooms__check"
                        onClick={(e) => e.preventDefault()}
                      >
                        {airport.code == selectedDepartureAirport?.code && (
                          <span className="configure-rooms__check--active">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="10"
                              height="10"
                              fill="currentColor"
                              className="bi bi-circle-fill"
                              viewBox="0 0 16 16"
                            >
                              <circle cx="8" cy="8" r="8" />
                            </svg>
                          </span>
                        )}
                      </a>
                      <div className="departure-airport__airport">
                        {airport.name} ({airport.code})
                      </div>
                      <div
                        className={`departure-airport__price ${
                          Math.sign(getPriceDifference(airport.code)) == -1
                            ? "departure-airport__price--less"
                            : "departure-airport__price--more"
                        }`}
                      >
                        {!(
                          flightPairs.filter(
                            (x) =>
                              first(x.outward.flightMetaData.flightLines)?.departureAirport ==
                                airport.code &&
                              first(x.outward.flightMetaData.flightLines)?.travelClass ==
                                selectedTravelClass
                          ).length > 0
                        ) ? (
                          <span className="no-flights-for-class">{t("NO_FLIGHTS_FOR_CLASS")}</span>
                        ) : (
                          Math.sign(getPriceDifference(airport.code)) != 0 &&
                          airport != selectedDepartureAirport &&
                          `${
                            Math.sign(getPriceDifference(airport.code)) == -1 ? "-" : "+"
                          } ${formatter.format(Math.abs(getPriceDifference(airport.code)))}`
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
          <div className="fly-in__item">
            <p className="fly-in__item-title">
              {t("ALTERNATE_FLIGHTS")}{" "}
              <span className="fly-in__item-title--option">
                {selectedDepartureAirport?.name} ({selectedDepartureAirport?.code})
              </span>
            </p>
            <div className="fly-in__item-content">
              <div className="flights-alternative">
                {filteredFlightPairs.map((flight, index) => (
                  <div
                    key={index}
                    className={`flights-alternative__item ${
                      flight.outward.isSelected ? "flights-alternative__item--selected" : ""
                    }`}
                  >
                    <div className="flights-alternative__header">
                      <div
                        className={`flights-alternative__price ${
                          Math.sign(
                            getPriceDifference(selectedDepartureAirport?.code ?? "", flight)
                          ) == -1
                            ? "flights-alternative__price--less"
                            : "flights-alternative__price--more"
                        }`}
                      >
                        {Math.sign(
                          getPriceDifference(selectedDepartureAirport?.code ?? "", flight)
                        ) != 0 &&
                          `${
                            Math.sign(
                              getPriceDifference(selectedDepartureAirport?.code ?? "", flight)
                            ) == -1
                              ? "-"
                              : "+"
                          } ${formatter.format(
                            Math.abs(
                              getPriceDifference(selectedDepartureAirport?.code ?? "", flight)
                            )
                          )}`}
                      </div>
                      <div className="flights-alternative__cta">
                        <button
                          className={`cta ${
                            selectedFlightPair?.outward.entryLineGuid ==
                              flight.outward.entryLineGuid &&
                            selectedFlightPair?.return.entryLineGuid == flight.return.entryLineGuid
                              ? "cta--selected"
                              : "cta--secondary"
                          }`}
                          onClick={(e) => selectFlight(flight)}
                        >
                          {selectedFlightPair?.outward.entryLineGuid ==
                            flight.outward.entryLineGuid &&
                          selectedFlightPair?.return.entryLineGuid == flight.return.entryLineGuid
                            ? t("SELECTED")
                            : t("SELECT")}
                        </button>
                      </div>
                    </div>
                    <div className="flights-alternative__body">
                      <div className="flights-alternative__outward">
                        <div className="flights-alternative__logo">
                          {getAirlineIcon(flight.outward) ? (
                            <img src={getAirlineIcon(flight.outward)} />
                          ) : (
                            <span>
                              {
                                first(flight.outward.flightMetaData.flightLines)
                                  ?.operatingAirlineDescription
                              }
                            </span>
                          )}
                        </div>
                        <div className="flights-alternative__type">{t("OUTWARD_FLIGHT")}</div>
                        <div className="flights-alternative__details">
                          <div>
                            <div className="flights-alternative__label">{t("DATE")}</div>
                            <div className="flights-alternative__value">
                              {format(
                                parse(
                                  flight.outward.startDateTime,
                                  "yyyy-MM-dd'T'HH:mm:ss'Z'",
                                  new Date()
                                ),
                                "dd/MM/yyyy"
                              )}
                            </div>
                          </div>
                          <div>
                            <div className="flights-alternative__label">{t("FLIGHT_NUMBER")}</div>
                            <div className="flights-alternative__value">
                              {first(flight.outward.flightMetaData.flightLines)?.number}
                            </div>
                          </div>
                          <div>
                            <div className="flights-alternative__label">{t("TYPE")}</div>
                            <div className="flights-alternative__value">
                              {flight.outward.flightMetaData.flightLines.length > 1
                                ? flight.outward.flightMetaData.flightLines.length -
                                  1 +
                                  " " +
                                  t("STOPOVERS")
                                : t("DIRECT")}
                              , {first(flight.outward.flightMetaData.flightLines)?.travelClass}
                            </div>
                          </div>
                        </div>
                        <div className="flights-alternative__flightinfo">
                          {flight.outward.flightMetaData.flightLines.map((flightLine, index) => (
                            <div key={index} className="flights-alternative__leg">
                              <div className="flights-alternative__leg-from">
                                <span className="flights-alternative__leg-airport">
                                  {flightLine.departureAirport}
                                  <div className="tooltip tooltip--airport-description">
                                    <span className="tooltip__title">
                                      {flightLine.departureAirportDescription}
                                    </span>
                                  </div>
                                </span>
                                <span className="flights-alternative__leg-time">
                                  {flightLine.departureTime}
                                </span>
                                <span className="flights-alternative__leg-date">
                                  {format(new Date(flightLine.departureDate), "dd/MM")}
                                </span>
                              </div>
                              <div className="flights-alternative__leg-duration">
                                <div className="flights-alternative__leg-flight">
                                  <Icon name="ui-flight" />
                                </div>
                              </div>
                              <div className="flights-alternative__leg-to">
                                <span className="flights-alternative__leg-airport">
                                  {flightLine.arrivalAirport}
                                  <div className="tooltip tooltip--airport-description">
                                    <span className="tooltip__title">
                                      {flightLine.arrivalAirportDescription}
                                    </span>
                                  </div>
                                </span>
                                <span className="flights-alternative__leg-time">
                                  {flightLine.arrivalTime}
                                </span>
                                <span className="flights-alternative__leg-date">
                                  {format(new Date(flightLine.arrivalDate), "dd/MM")}
                                  <FlyInWarnings
                                    warnings={getWarnings(flight, flightLine, true)}
                                  ></FlyInWarnings>
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="flights-alternative__retour">
                        <div className="flights-alternative__logo">
                          {getAirlineIcon(flight.return) ? (
                            <img src={getAirlineIcon(flight.return)} />
                          ) : (
                            <span>
                              {
                                first(flight.outward.flightMetaData.flightLines)
                                  ?.operatingAirlineDescription
                              }
                            </span>
                          )}
                        </div>
                        <div className="flights-alternative__type">{t("RETURN_FLIGHT")}</div>
                        <div className="flights-alternative__details">
                          <div>
                            <div className="flights-alternative__label">{t("DATE")}</div>
                            <div className="flights-alternative__value">
                              {format(
                                parse(
                                  flight.return.startDateTime,
                                  "yyyy-MM-dd'T'HH:mm:ss'Z'",
                                  new Date()
                                ),
                                "dd/MM/yyyy"
                              )}
                            </div>
                          </div>
                          <div>
                            <div className="flights-alternative__label">{t("FLIGHT_NUMBER")}</div>
                            <div className="flights-alternative__value">
                              {first(flight.return.flightMetaData.flightLines)?.number}
                            </div>
                          </div>
                          <div>
                            <div className="flights-alternative__label">{t("TYPE")}</div>
                            <div className="flights-alternative__value">
                              {flight.return.flightMetaData.flightLines.length > 1
                                ? flight.return.flightMetaData.flightLines.length -
                                  1 +
                                  " " +
                                  t("STOPOVERS")
                                : t("DIRECT")}
                              , {first(flight.return.flightMetaData.flightLines)?.travelClass}
                            </div>
                          </div>
                        </div>
                        <div className="flights-alternative__flightinfo">
                          {flight.return.flightMetaData.flightLines.map((flightLine, index) => (
                            <div key={index} className="flights-alternative__leg">
                              <div className="flights-alternative__leg-from">
                                <span className="flights-alternative__leg-airport">
                                  {flightLine.departureAirport}
                                  <div className="tooltip tooltip--airport-description">
                                    <span className="tooltip__title">
                                      {flightLine.departureAirportDescription}
                                    </span>
                                  </div>
                                </span>
                                <span className="flights-alternative__leg-time">
                                  {flightLine.departureTime}
                                </span>
                                <span className="flights-alternative__leg-date">
                                  {format(new Date(flightLine.departureDate), "dd/MM")}
                                </span>
                              </div>
                              <div className="flights-alternative__leg-duration">
                                <div className="flights-alternative__leg-flight">
                                  <Icon name="ui-flight" />
                                </div>
                              </div>
                              <div className="flights-alternative__leg-to">
                                <span className="flights-alternative__leg-airport">
                                  {flightLine.arrivalAirport}
                                  <div className="tooltip tooltip--airport-description">
                                    <span className="tooltip__title">
                                      {flightLine.arrivalAirportDescription}
                                    </span>
                                  </div>
                                </span>
                                <span className="flights-alternative__leg-time">
                                  {flightLine.arrivalTime}
                                </span>
                                <span className="flights-alternative__leg-date">
                                  {format(new Date(flightLine.arrivalDate), "dd/MM")}
                                  <FlyInWarnings
                                    warnings={getWarnings(flight, flightLine, false)}
                                  ></FlyInWarnings>
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="fly-in__footer--bottom">
          <div className="fly-in__footer fly-in__footer--desktop">
            <div className="fly-in__footer-cta">
              <button className="cta" onClick={() => confirmOptions()}>
                {t("CONFIRM_AND_CLOSE")}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="#FFFFFF"
                  className="bi bi-check-lg"
                  viewBox="0 0 16 16"
                  style={{ marginLeft: "1rem" }}
                >
                  <path d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425a.247.247 0 0 1 .02-.022Z" />
                </svg>
              </button>
            </div>
          </div>
          <div className="fly-in__footer fly-in__footer--mobile">
            <div className="fly-in__footer-cta">
              <button className="cta" onClick={() => confirmOptions()}>
                {t("CONFIRM")}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="#FFFFFF"
                  className="bi bi-check-lg"
                  viewBox="0 0 16 16"
                  style={{ marginLeft: "1rem" }}
                >
                  <path d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425a.247.247 0 0 1 .02-.022Z" />
                </svg>
              </button>
            </div>
          </div>
          <div className="fly-in__footer">
            <div className="fly-in__footer-row fly-in__footer-row--moreprice">
              <div className="fly-in__footer-label">{t("ADDITIONAL_COST")}</div>
              <div className="fly-in__footer-price">{`${
                Math.sign(getTotalPriceDiff()) == -1 ? "-" : "+"
              } ${formatter.format(Math.abs(getTotalPriceDiff()))}`}</div>
            </div>
            <div className="fly-in__footer-row fly-in__footer-row--totalprice">
              <div className="fly-in__footer-label">
                {productState?.price?.defaultPrice && <>{t("TOTAL_PRICE")}</>}
              </div>
              <div className="fly-in__footer-price">
                {productState?.price?.defaultPrice && (
                  <>{formatter.format(productState?.price?.defaultPrice + getTotalPriceDiff())}</>
                )}
                {!productState?.price?.defaultPrice && productState?.estimatedPrice?.promoPrice && (
                  <>
                    {t("STARTING_FROM_SHORT")}{" "}
                    {formatter.format(productState?.estimatedPrice?.promoPrice)}{" "}
                    {t("PER_PERSON_SHORT")}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlightsFlyIn;
